import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Terms() {
  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="terms-page">
      <SEO
        title="Terms of Service"
        description="Serverlistr Terms of Service — the rules for using our Discord server directory and bot."
        path="/terms"
      />
      <Breadcrumbs items={[{ name: "Terms of Service" }]} />
      <h1 className="font-heading text-3xl sm:text-4xl font-black">Terms of Service</h1>
      <p className="mt-2 text-xs text-zinc-500 font-mono">Last updated: {new Date().toISOString().slice(0, 10)}</p>

      <div className="mt-8 space-y-8 text-sm text-zinc-300 leading-relaxed">
        <Section title="1. Acceptance">
          By accessing or using Serverlistr (serverlistr.net) or the Serverlistr Discord bot,
          you agree to these Terms. If you do not agree, do not use the service.
        </Section>

        <Section title="2. What Serverlistr is">
          Serverlistr is a public directory of Discord servers. Users may sign in with a
          Discord account, submit a server they own or moderate, and browse listings
          submitted by others. We are not affiliated with Discord Inc.
        </Section>

        <Section title="3. Your account">
          You need a Discord account to sign in. You are responsible for keeping your
          account secure. You must be at least 13 years old, or the minimum age required
          by Discord in your country — whichever is higher.
        </Section>

        <Section title="4. Listing your server">
          To list a Discord server you must have the &quot;Manage Server&quot; permission on it.
          Ownership is verified by adding the Serverlistr bot to the server.
          You are responsible for the content of your listing — name, description,
          categories, tags, banner. You grant Serverlistr a non-exclusive license to
          display that content on our site.
        </Section>

        <Section title="5. The Serverlistr bot">
          Our bot requires only the &quot;Create Invite&quot; permission and is used to (a) verify
          ownership, (b) fetch public server metadata (name, icon, banner, approximate
          member count), and (c) generate a permanent invite. The bot does not read
          messages, does not require privileged intents, and does not store the contents
          of any conversations.
        </Section>

        <Section title="6. Prohibited content">
          You may not list any server whose primary purpose is:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>Illegal activity (including CSAM, drug trafficking, weapons sales)</li>
            <li>Harassment, doxxing, hate speech, or targeted abuse</li>
            <li>Malware distribution, phishing, or crypto/token scams</li>
            <li>Piracy or unauthorized distribution of copyrighted material</li>
            <li>Explicit sexual content involving minors under any circumstances</li>
            <li>Selling or trading Discord accounts, Nitro, or platform violations</li>
          </ul>
          We may remove any listing at our sole discretion. Repeat or severe violations
          may result in a permanent ban.
        </Section>

        <Section title="7. Payments">
          Featured listing packages are one-time payments processed by Stripe. All sales
          are final. Serverlistr does not store your card details — Stripe handles that
          under its own terms. If your featured listing is removed for a Terms violation,
          no refund is issued.
        </Section>

        <Section title="8. Ownership disputes">
          If two people claim to own the same server, the account with active
          &quot;Manage Server&quot; permission at the time of dispute wins. Fraudulent
          re-submissions after a takedown may result in a ban.
        </Section>

        <Section title="9. Copyright & DMCA">
          If you believe content on Serverlistr infringes your copyright, message us in our
          <a
            href="https://discord.gg/HWQGPR6nR4"
            target="_blank"
            rel="noreferrer"
            className="text-blurple hover:text-blurple-hover ml-1"
          >
            support Discord
          </a>{" "}
          with (a) your contact info,
          (b) identification of the copyrighted work, (c) the URL of the infringing listing,
          and (d) a good-faith statement that use is unauthorized. We remove valid claims promptly.
        </Section>

        <Section title="10. Disclaimers">
          Serverlistr is provided &quot;as-is&quot; without warranties of any kind. We do not
          guarantee uptime, accuracy of any listing, or the safety of any Discord server
          listed. You use third-party Discord servers at your own risk.
        </Section>

        <Section title="11. Limitation of liability">
          To the maximum extent permitted by law, Serverlistr and its operators are not
          liable for any indirect, incidental, or consequential damages arising from your
          use of the service. Our total liability is limited to the amount you have paid
          us in the last twelve months.
        </Section>

        <Section title="12. Termination">
          We may suspend or delete your account and listings at any time for a Terms
          violation. You may delete your account at any time by contacting us.
        </Section>

        <Section title="13. Changes">
          We may update these Terms. Material changes will be reflected by updating the
          &quot;Last updated&quot; date. Continued use after changes constitutes acceptance.
        </Section>

        <Section title="14. Contact">
          Questions about these Terms? Join our
          <a
            href="https://discord.gg/HWQGPR6nR4"
            target="_blank"
            rel="noreferrer"
            className="text-blurple hover:text-blurple-hover ml-1"
          >
            support Discord
          </a>
          .
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-lg text-white mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
